import React, {
    useState,
    useLayoutEffect,
    useCallback,
    forwardRef,
} from 'react';
import {
    TextTarget,
    TargetType,
    ResponsiveTargetType,
    FixedTarget,
} from 'contexts/OnboardingContext/OnboardingContext.type';
import {
    isATextTarget,
    isAPositionTarget,
} from 'contexts/OnboardingContext/OnboardingContext.utils';
import { Breakpoint, Breakpoints } from '@landr/maestro';
import { createContainer } from 'utils/createContainer';
import { isElementHasDimension } from 'utils/isElementHasDimension';
import { useMutationObserver } from 'hooks/useMutationObserver';
import useGetCurrentBreakpoint from 'hooks/useGetCurrentBreakpoint/useGetCurrentBreakpoint';
import { Styled } from './OnboardingCoachmark.style';

type BreakpointType = Breakpoint | 'default';

export const containerId = 'onboarding-container';

const queryByText = (target: TextTarget): Element | undefined => {
    return Array.from(document.querySelectorAll(target.parent)).find(
        (el) => isElementHasDimension(el) && el.textContent === target.text,
    );
};

const AnchorFixed = forwardRef<
    HTMLDivElement,
    React.PropsWithChildren<FixedTarget>
>(({ children, ...target }, ref): JSX.Element => {
    const fixedPosition = {
        top: 'auto',
        right: 'auto',
        bottom: 'auto',
        left: 'auto',
        ...target,
    };

    return (
        <Styled.AnchorFixed ref={ref} {...fixedPosition}>
            {children}
        </Styled.AnchorFixed>
    );
});

const onboardingContainerId = 'onboarding-container';

export const getTarget = (
    target: TargetType,
): {
    targetElement: Element | undefined;
    Anchor: JSX.Element;
} => {
    let tentativeTarget = undefined;
    let tentativeAnchor = undefined;

    if (typeof target === 'string') {
        tentativeTarget = Array.from(document.querySelectorAll(target)).find(
            (el) => isElementHasDimension(el),
        );
    }

    if (isATextTarget(target)) {
        tentativeTarget = queryByText(target);
    }

    if (isAPositionTarget(target)) {
        tentativeTarget =
            document.querySelector(`#${onboardingContainerId}`) ||
            createContainer(onboardingContainerId);
        tentativeAnchor = <AnchorFixed {...target} />;
    }

    const targetElement = tentativeTarget;
    const Anchor = tentativeAnchor || <Styled.AnchorAbsolute />;

    return { targetElement, Anchor };
};

// This evalute current breakpoint and return the first valid target of a step
export const useResponsiveTarget = (
    target: ResponsiveTargetType | undefined,
): TargetType | undefined => {
    const breakpointsObject = {
        ...Breakpoints,
        default: 1000000,
        unknown: 1000000,
    };

    const currentBreakpoint = useGetCurrentBreakpoint();

    const [currentTargetBreakpoint, setCurrentTargetBreakpoint] =
        useState<BreakpointType>('default');

    useLayoutEffect(() => {
        if (currentBreakpoint && target) {
            const targetBreakpoint = Object.keys(target);

            // Filter breakpoint smaller than currentBreakpoint
            const filteredTargetBreakpoint = targetBreakpoint.filter(
                (breakpoint) => {
                    return (
                        breakpointsObject[breakpoint as BreakpointType] >=
                        breakpointsObject[currentBreakpoint]
                    );
                },
            );

            // Sort filtered breakpoint from smaller to bigger
            const sortedTargetBreakpoint = filteredTargetBreakpoint.sort(
                (a, b) => {
                    const breakpointA = breakpointsObject[a as BreakpointType];
                    const breakpointB = breakpointsObject[b as BreakpointType];

                    return breakpointA - breakpointB;
                },
            );

            setCurrentTargetBreakpoint(
                sortedTargetBreakpoint[0] as BreakpointType,
            );
        }
    }, [currentBreakpoint, target]);

    return target && target[currentTargetBreakpoint];
};

export const useGetTarget = (
    target: ResponsiveTargetType | undefined,
): {
    currentTarget: TargetType | undefined;
    targetElement: Element | undefined;
    Anchor: JSX.Element | undefined;
} => {
    const currentTarget = useResponsiveTarget(target);
    const isNotAPositionTarget = !isAPositionTarget(currentTarget);

    const [targetElement, setTargetElement] = useState<Element>();
    const [Anchor, setAnchor] = useState<JSX.Element>();

    useLayoutEffect(() => {
        if (currentTarget) {
            const { targetElement, Anchor } = getTarget(currentTarget);

            setTargetElement(targetElement);
            setAnchor(Anchor);
        }
    }, [currentTarget]);

    const mutationObserverCallback = useCallback(() => {
        if (currentTarget && isNotAPositionTarget) {
            const { targetElement, Anchor } = getTarget(currentTarget);

            setTargetElement(targetElement);
            setAnchor(Anchor);
        }
    }, [currentTarget, isNotAPositionTarget]);

    /**
     * Observe the entire document.body for mutation,
     * but the hook debounce the callback to mitigate performance load.
     * We had to observe the entire DOM because it was impossible to guarantee
     * that any parent of a target won't be remove at some point from the DOM, disconnecting the mutation observer.
     */
    useMutationObserver(document.body, mutationObserverCallback);

    return { currentTarget, targetElement, Anchor };
};
